/**
 * MDX compile cache
 *
 * next-mdx-remote/rsc's `MDXRemote` recompiles its source on every render.
 * Every route is dynamic (the root layout reads the CSP nonce and the cut
 * cookie), so each blog article request re-ran remark/rehype, Shiki and KaTeX
 * over the whole post. This module compiles once per (options, source) pair
 * and reuses the result for the life of the server instance.
 *
 * Compilation and hydration mirror next-mdx-remote's `compileMDX` exactly
 * (`serialize(source, options, true)`, then the `Function` constructor with
 * `{ opts: jsxRuntime, frontmatter, ...scope }`), so output is unchanged.
 */
import * as React from 'react';
import { serialize } from 'next-mdx-remote/serialize';
import type { MDXRemoteProps } from 'next-mdx-remote/rsc';
import * as prodJsxRuntime from 'react/jsx-runtime';
import * as devJsxRuntime from 'react/jsx-dev-runtime';

type MDXComponents = MDXRemoteProps['components'];
type MDXRemoteOptions = MDXRemoteProps['options'];
type MDXContent = React.ComponentType<{ components?: MDXComponents }>;

/** Upper bound per options object; posts + legal docs + project pages fit well under it. */
export const MDX_CACHE_MAX_ENTRIES = 256;

// serialize() compiles with `development: NODE_ENV !== 'production'`, and
// next-mdx-remote's jsx-runtime.cjs picks the matching runtime the same way.
const jsxRuntime = process.env.NODE_ENV === 'production' ? prodJsxRuntime : devJsxRuntime;

// Keyed by options identity, so callers must pass a stable (module-level) object.
const caches = new WeakMap<object, Map<string, Promise<MDXContent>>>();
const NO_OPTIONS = {};

function hydrate(compiledSource: string, frontmatter: unknown, scope: object): MDXContent {
  const fullScope: Record<string, unknown> = { opts: jsxRuntime, frontmatter, ...scope };
  const hydrateFn = Reflect.construct(Function, [...Object.keys(fullScope), compiledSource]) as (
    ...args: unknown[]
  ) => { default: MDXContent };
  return hydrateFn.apply(hydrateFn, Object.values(fullScope)).default;
}

async function compile(source: string, options: MDXRemoteOptions | undefined): Promise<MDXContent> {
  const { compiledSource, frontmatter, scope } = await serialize(source, options, true);
  return hydrate(compiledSource, frontmatter, scope);
}

/**
 * Returns the compiled MDX content component for `source`, compiling at most
 * once per (options, source). Failed compiles are not cached.
 */
export function getMDXContent(
  source: string,
  options?: MDXRemoteOptions
): Promise<MDXContent> {
  const key = options ?? NO_OPTIONS;
  let cache = caches.get(key);
  if (!cache) {
    cache = new Map();
    caches.set(key, cache);
  }

  const hit = cache.get(source);
  if (hit) {
    // Refresh recency: Map iteration order is insertion order
    cache.delete(source);
    cache.set(source, hit);
    return hit;
  }

  const pending = compile(source, options);
  cache.set(source, pending);
  const entries = cache;
  pending.catch(() => {
    if (entries.get(source) === pending) entries.delete(source);
  });

  if (cache.size > MDX_CACHE_MAX_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }

  return pending;
}

/**
 * Drop-in replacement for next-mdx-remote/rsc's `MDXRemote` (string sources
 * only) that reuses compiled output across requests.
 */
export async function CachedMDXRemote({
  source,
  options,
  components,
}: {
  source: string;
  options?: MDXRemoteOptions;
  components?: MDXComponents;
}) {
  const Content = await getMDXContent(source, options);
  return <Content components={components} />;
}
